import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Database, 
  Play, 
  Pause, 
  RefreshCw, 
  Settings, 
  BarChart3,
  Clock,
  HardDrive,
  Eye,
  Edit3,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

interface MemoryRDIStatus {
  liveContext: {
    size: number;
    maxSize: number;
    hitRate: number;
    missRate: number;
    avgLatency: number;
    status: 'active' | 'inactive' | 'error';
  };
  snapshots: {
    count: number;
    latest: string;
    size: number;
    compressionRatio: number;
    status: 'active' | 'inactive' | 'error';
  };
  replayBuffer: {
    size: number;
    maxSize: number;
    events: number;
    oldestEvent: string;
    newestEvent: string;
    status: 'active' | 'inactive' | 'error';
  };
  vectorIndex: {
    size: number;
    dimensions: number;
    similarityThreshold: number;
    embeddingRate: number;
    queryLatency: number;
    status: 'active' | 'inactive' | 'error';
  };
  deadLetterQueue: {
    size: number;
    oldestMessage: string;
    errorTypes: Array<{
      type: string;
      count: number;
    }>;
    status: 'active' | 'inactive' | 'error';
  };
  consumers: Array<{
    id: string;
    name: string;
    status: 'running' | 'stopped' | 'error';
    lag: number;
    throughput: number;
    lastProcessed: string;
  }>;
}

interface MemoryRDIConfig {
  liveContext: {
    maxSize: number;
    ttl: number;
    enableCompression: boolean;
    compressionLevel: number;
  };
  snapshots: {
    interval: number;
    retention: number;
    enableCompression: boolean;
    compressionLevel: number;
  };
  replayBuffer: {
    maxSize: number;
    ttl: number;
    enableCompression: boolean;
    compressionLevel: number;
  };
  vectorIndex: {
    dimensions: number;
    similarityThreshold: number;
    embeddingModel: string;
    batchSize: number;
  };
  consumers: {
    batchSize: number;
    parallelWorkers: number;
    timeout: number;
    retryAttempts: number;
  };
}

export const MemoryRDIControlPanel: React.FC = () => {
  const [status, setStatus] = useState<MemoryRDIStatus | null>(null);
  const [config, setConfig] = useState<MemoryRDIConfig | null>(null);
  const [editingConfig, setEditingConfig] = useState<MemoryRDIConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'live-context' | 'snapshots' | 'replay' | 'vector' | 'consumers' | 'config'>('overview');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Simulate loading Memory RDI data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setStatus({
          liveContext: {
            size: 8500,
            maxSize: 10000,
            hitRate: 94.2,
            missRate: 5.8,
            avgLatency: 12.5,
            status: 'active'
          },
          snapshots: {
            count: 24,
            latest: '2025-01-01T12:00:00Z',
            size: 2048000,
            compressionRatio: 0.75,
            status: 'active'
          },
          replayBuffer: {
            size: 15000,
            maxSize: 20000,
            events: 125000,
            oldestEvent: '2024-12-31T00:00:00Z',
            newestEvent: '2025-01-01T12:00:00Z',
            status: 'active'
          },
          vectorIndex: {
            size: 50000,
            dimensions: 768,
            similarityThreshold: 0.85,
            embeddingRate: 150.5,
            queryLatency: 45.2,
            status: 'active'
          },
          deadLetterQueue: {
            size: 3,
            oldestMessage: '2025-01-01T11:45:00Z',
            errorTypes: [
              { type: 'embedding_timeout', count: 2 },
              { type: 'validation_error', count: 1 }
            ],
            status: 'active'
          },
          consumers: [
            {
              id: 'consumer-1',
              name: 'Live Context Ingestor',
              status: 'running',
              lag: 150,
              throughput: 1250.5,
              lastProcessed: '2025-01-01T12:00:00Z'
            },
            {
              id: 'consumer-2',
              name: 'Snapshot Builder',
              status: 'running',
              lag: 0,
              throughput: 45.2,
              lastProcessed: '2025-01-01T11:58:00Z'
            },
            {
              id: 'consumer-3',
              name: 'Vector Indexer',
              status: 'running',
              lag: 75,
              throughput: 89.7,
              lastProcessed: '2025-01-01T11:59:30Z'
            }
          ]
        });

        setConfig({
          liveContext: {
            maxSize: 10000,
            ttl: 3600000,
            enableCompression: true,
            compressionLevel: 6
          },
          snapshots: {
            interval: 3600000,
            retention: 2592000000,
            enableCompression: true,
            compressionLevel: 9
          },
          replayBuffer: {
            maxSize: 20000,
            ttl: 86400000,
            enableCompression: true,
            compressionLevel: 6
          },
          vectorIndex: {
            dimensions: 768,
            similarityThreshold: 0.85,
            embeddingModel: 'text-embedding-ada-002',
            batchSize: 100
          },
          consumers: {
            batchSize: 1000,
            parallelWorkers: 4,
            timeout: 300000,
            retryAttempts: 3
          }
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load Memory RDI data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'running':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'inactive':
      case 'stopped':
        return <Pause size={16} className="text-yellow-400" />;
      case 'error':
        return <AlertTriangle size={16} className="text-red-400" />;
      default:
        return <Minus size={16} className="text-slate-400" />;
    }
  };

  const handleAction = async (action: string, target?: string) => {
    console.log(`Performing ${action} on ${target || 'system'}`);
    // In real implementation, this would call the appropriate API
  };

  const handleConfigUpdate = (section: keyof MemoryRDIConfig, key: string, value: any) => {
    if (!editingConfig) return;
    
    setEditingConfig(prev => ({
      ...prev!,
      [section]: {
        ...prev![section],
        [key]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
        </div>
        <div className="h-96 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle size={48} className="text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Failed to load Memory RDI data</h3>
        <p className="text-slate-400 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!status || !config) return null;

  return (
    <div className="space-y-6">
      {/* Memory RDI Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Live Context</h3>
            <Zap size={20} className="text-yellow-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {status.liveContext.size.toLocaleString()}
          </div>
          <p className="text-sm text-slate-400">
            {status.liveContext.hitRate}% hit rate
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Snapshots</h3>
            <HardDrive size={20} className="text-blue-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {status.snapshots.count}
          </div>
          <p className="text-sm text-slate-400">
            {(status.snapshots.size / 1024 / 1024).toFixed(1)} MB
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Replay Buffer</h3>
            <Clock size={20} className="text-green-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {status.replayBuffer.events.toLocaleString()}
          </div>
          <p className="text-sm text-slate-400">
            events buffered
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Vector Index</h3>
            <BarChart3 size={20} className="text-purple-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {status.vectorIndex.size.toLocaleString()}
          </div>
          <p className="text-sm text-slate-400">
            {status.vectorIndex.embeddingRate}/s embeddings
          </p>
        </motion.div>
      </div>

      {/* Memory RDI Control Tabs */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="evolut-metric-card p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6">Memory RDI Controls</h3>
          <nav className="space-y-2">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'live-context', label: 'Live Context', icon: Zap },
              { id: 'snapshots', label: 'Snapshots', icon: HardDrive },
              { id: 'replay', label: 'Replay Buffer', icon: Clock },
              { id: 'vector', label: 'Vector Index', icon: BarChart3 },
              { id: 'consumers', label: 'Consumers', icon: Database },
              { id: 'config', label: 'Configuration', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-slate-700/50 text-white'
                    : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                }`}
              >
                <tab.icon size={20} />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="lg:col-span-3"
        >
          {activeTab === 'overview' && (
            <div className="evolut-metric-card p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Memory RDI Overview</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-white font-medium">System Health</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Zap size={20} className="text-yellow-400" />
                        <span className="text-white">Live Context</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(status.liveContext.status)}
                        <span className="text-sm text-slate-400">
                          {status.liveContext.size}/{status.liveContext.maxSize}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <HardDrive size={20} className="text-blue-400" />
                        <span className="text-white">Snapshots</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(status.snapshots.status)}
                        <span className="text-sm text-slate-400">
                          {status.snapshots.count} snapshots
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Clock size={20} className="text-green-400" />
                        <span className="text-white">Replay Buffer</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(status.replayBuffer.status)}
                        <span className="text-sm text-slate-400">
                          {status.replayBuffer.events.toLocaleString()} events
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <BarChart3 size={20} className="text-purple-400" />
                        <span className="text-white">Vector Index</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(status.vectorIndex.status)}
                        <span className="text-sm text-slate-400">
                          {status.vectorIndex.size.toLocaleString()} vectors
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-white font-medium">Performance Metrics</h4>
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white">Live Context Hit Rate</span>
                        <span className="text-green-400 font-medium">{status.liveContext.hitRate}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-green-400 h-2 rounded-full" 
                          style={{ width: `${status.liveContext.hitRate}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white">Avg Query Latency</span>
                        <span className="text-blue-400 font-medium">{status.vectorIndex.queryLatency}ms</span>
                      </div>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white">Embedding Rate</span>
                        <span className="text-purple-400 font-medium">{status.vectorIndex.embeddingRate}/s</span>
                      </div>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white">Dead Letter Queue</span>
                        <span className="text-red-400 font-medium">{status.deadLetterQueue.size} messages</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'live-context' && (
            <div className="evolut-metric-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Live Context Management</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleAction('refresh', 'live-context')}
                    className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                    title="Refresh Live Context"
                  >
                    <RefreshCw size={16} className="text-blue-400" />
                  </button>
                  <button
                    onClick={() => handleAction('clear', 'live-context')}
                    className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                    title="Clear Live Context"
                  >
                    <X size={16} className="text-red-400" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Current Status</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Size</span>
                      <span className="text-white">{status.liveContext.size.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Max Size</span>
                      <span className="text-white">{status.liveContext.maxSize.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Hit Rate</span>
                      <span className="text-green-400">{status.liveContext.hitRate}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Miss Rate</span>
                      <span className="text-red-400">{status.liveContext.missRate}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Avg Latency</span>
                      <span className="text-blue-400">{status.liveContext.avgLatency}ms</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-white font-medium">Configuration</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Max Size</span>
                      <span className="text-white">{config.liveContext.maxSize.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">TTL</span>
                      <span className="text-white">{config.liveContext.ttl / 1000}s</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Compression</span>
                      <span className="text-white">{config.liveContext.enableCompression ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Compression Level</span>
                      <span className="text-white">{config.liveContext.compressionLevel}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'consumers' && (
            <div className="evolut-metric-card p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Consumer Management</h3>
              
              <div className="space-y-4">
                {status.consumers.map((consumer) => (
                  <div key={consumer.id} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <Database size={20} className="text-blue-400" />
                        <div>
                          <h4 className="text-white font-medium">{consumer.name}</h4>
                          <p className="text-sm text-slate-400">
                            ID: {consumer.id}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(consumer.status)}
                          <span className="text-sm text-slate-400 capitalize">{consumer.status}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-slate-400">Lag: {consumer.lag}ms</div>
                          <div className="text-sm text-slate-400">Throughput: {consumer.throughput.toFixed(1)}/s</div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleAction('start', consumer.id)}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                            title="Start Consumer"
                          >
                            <Play size={16} className="text-green-400" />
                          </button>
                          <button
                            onClick={() => handleAction('stop', consumer.id)}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                            title="Stop Consumer"
                          >
                            <Pause size={16} className="text-red-400" />
                          </button>
                          <button
                            onClick={() => handleAction('restart', consumer.id)}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                            title="Restart Consumer"
                          >
                            <RefreshCw size={16} className="text-yellow-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div className="evolut-metric-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Configuration</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingConfig(config)}
                    className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                    title="Edit Configuration"
                  >
                    <Edit3 size={16} className="text-yellow-400" />
                  </button>
                  <button
                    onClick={() => handleAction('save-config')}
                    className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                    title="Save Configuration"
                  >
                    <Save size={16} className="text-green-400" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Live Context Config</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Max Size</label>
                      <input
                        type="number"
                        value={editingConfig?.liveContext.maxSize || config.liveContext.maxSize}
                        onChange={(e) => handleConfigUpdate('liveContext', 'maxSize', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">TTL (ms)</label>
                      <input
                        type="number"
                        value={editingConfig?.liveContext.ttl || config.liveContext.ttl}
                        onChange={(e) => handleConfigUpdate('liveContext', 'ttl', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-white font-medium">Consumer Config</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Batch Size</label>
                      <input
                        type="number"
                        value={editingConfig?.consumers.batchSize || config.consumers.batchSize}
                        onChange={(e) => handleConfigUpdate('consumers', 'batchSize', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Parallel Workers</label>
                      <input
                        type="number"
                        value={editingConfig?.consumers.parallelWorkers || config.consumers.parallelWorkers}
                        onChange={(e) => handleConfigUpdate('consumers', 'parallelWorkers', parseInt(e.target.value))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};